import React, { useEffect, useState } from "react";
import { PokemonBoxData, UpdateBoxInput } from "types/web-app";
import { Dialog } from "@/components/ui/dialog/dialog";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { CheckIcon, RemoveIcon } from "@/icons";

interface BoxEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  box: PokemonBoxData;
  onSave: (input: UpdateBoxInput) => Promise<void>;
}

const BOX_ICONS = [
  "box",
  "bug",
  "card",
  "dark",
  "dice",
  "dragon",
  "electric",
  "eye",
  "fairy",
  "fighting",
  "fire",
  "flying",
  "ghost",
  "grass",
  "ground",
  "hanger",
  "hat",
  "heart",
  "ice",
  "normal",
  "poison",
  "psychic",
  "puzzle",
  "rock",
  "shopping-basket",
  "steel",
  "swap",
  "unknown",
  "water",
  "lock",
];

const BOX_COLORS = [
  "#3b2c35", // Dark Gray
  "#6c447d", // Purple
  "#a8a878", // Normal
  "#f08030", // Fire
  "#6890f0", // Water
  "#f8d030", // Electric
  "#78c850", // Grass
  "#98d8d8", // Ice
  "#c03028", // Fighting
  "#a040a0", // Poison
  "#e0c068", // Ground
  "#a890f0", // Flying
  "#f85888", // Psychic
  "#a8b820", // Bug
  "#b8a038", // Rock
  "#705898", // Ghost
  "#7038f8", // Dragon
  "#705848", // Dark
  "#b8b8d0", // Steel
  "#ee99ac", // Fairy
];

export const BoxEditModal = ({
  isOpen,
  onClose,
  box,
  onSave,
}: BoxEditModalProps) => {
  const [name, setName] = useState(box.name);
  const [color, setColor] = useState(box.color || "#FFFFFF");
  const [icon, setIcon] = useState(box.icon || "box");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(box.name);
      setColor(box.color || "#FFFFFF");
      setIcon(box.icon || "box");
    }
  }, [box, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    try {
      await onSave({
        box_id: box.id,
        name,
        color,
        icon,
      });
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <StandardFrame
        variant="purple"
        className="w-[90%] max-w-md max-h-[90vh] flex flex-col gap-4 overflow-hidden"
      >
        <div className="flex items-center justify-between border-b border-white/10 pb-2">
          <h3 className="text-lg font-bold text-white uppercase tracking-wider">
            Edit Box
          </h3>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <RemoveIcon width={32} height={32} className="invert" />
          </button>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto pr-1">
          {/* Name Field */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-pk-gray-4 uppercase">
              Box Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-black/40 border border-white/20 rounded px-3 py-2 text-white placeholder-white/50 focus:outline-none focus:border-pk-orange-4"
              placeholder="Enter box name..."
            />
          </div>

          {/* Color Selection */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-pk-gray-4 uppercase">
              Box Color
            </label>
            <div className="flex flex-wrap gap-2 p-2 bg-black/20 rounded">
              {BOX_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c
                      ? "border-white scale-110 shadow-lg"
                      : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {/* Icon Selection */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-pk-gray-4 uppercase">
              Box Icon
            </label>
            <div className="grid grid-cols-6 gap-2 p-2 bg-black/20 rounded max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20">
              {BOX_ICONS.map((i) => (
                <button
                  key={i}
                  onClick={() => setIcon(i)}
                  className={`aspect-square flex items-center justify-center p-1 rounded border-2 transition-all ${
                    icon === i
                      ? "bg-pk-orange-4/30 border-pk-orange-4"
                      : "bg-black/20 border-transparent hover:bg-black/40"
                  }`}
                >
                  <img
                    src={`/assets/icons/boxes/${i}.svg`}
                    alt={i}
                    className="w-full h-full object-contain"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-2 pt-2 border-t border-white/10">
          <SimpleButton onClick={onClose} variant="gray" className="flex-1">
            Cancel
          </SimpleButton>
          <SimpleButton
            onClick={handleSave}
            variant="green"
            className="flex-1"
            disabled={isSaving || !name.trim()}
          >
            {isSaving ? (
              "Saving..."
            ) : (
              <div className="flex items-center justify-center gap-2">
                <CheckIcon width={20} height={20} className="invert" />
                Save
              </div>
            )}
          </SimpleButton>
        </div>
      </StandardFrame>
    </Dialog>
  );
};
